import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  Paper,
  Portal,
  Tooltip,
} from '@mui/material';
import { EditOutlined as EditOutlinedIcon } from '@mui/icons-material';
import { TouchPoint } from '../../models/types';
import DenseEntityCardBody from '../cards/DenseEntityCardBody';
import { buildTouchPointCardModel } from '../../presentation/cards/models';

interface TouchPointPreviewLookups {
  origin?: { originId: string; address: string; zip?: string; lat: number; lng: number; hourStart?: string; hourEnd?: string };
  clients?: Record<string, { clientId: string; address: string; lat: number; lng: number; hourStart?: string; hourEnd?: string }>;
  drops?: Record<string, { dropId: string; address: string; lat: number; lng: number; hourStart?: string; hourEnd?: string }>;
}

interface TouchPointPreviewOverlayProps {
  touchPoint: TouchPoint | null;
  anchorClientPosition: { x: number; y: number } | null;
  tenant?: string;
  lookups?: TouchPointPreviewLookups;
  mode: 'light' | 'dark';
  zIndex?: number;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onStartGeoCorrection?: (touchPoint: TouchPoint) => void;
}

const DEFAULT_CARD_SIZE = {
  width: 360,
  height: 260,
};

function isVerifyPincodeTouchPoint(touchPoint: TouchPoint | null | undefined): boolean {
  return touchPoint?.clusterTag === 'VERIFY_PINCODE';
}

function resolveViewportOverlayPosition(
  anchor: { x: number; y: number } | null,
  size: { width: number; height: number }
): { left: number; top: number } {
  const fallback = { left: 16, top: 16 };
  if (!anchor || typeof window === 'undefined') {
    return fallback;
  }

  const gap = 12;
  let left = anchor.x + gap;
  let top = anchor.y + gap;

  if (left + size.width > window.innerWidth - gap) {
    left = anchor.x - size.width - gap;
  }
  if (top + size.height > window.innerHeight - gap) {
    top = anchor.y - size.height - gap;
  }

  left = Math.min(Math.max(gap, left), Math.max(gap, window.innerWidth - size.width - gap));
  top = Math.min(Math.max(gap, top), Math.max(gap, window.innerHeight - size.height - gap));
  return { left, top };
}

const TouchPointPreviewOverlay: React.FC<TouchPointPreviewOverlayProps> = ({
  touchPoint,
  anchorClientPosition,
  tenant,
  lookups,
  mode,
  zIndex = 1600,
  onMouseEnter,
  onMouseLeave,
  onStartGeoCorrection,
}) => {
  const [cardSize, setCardSize] = useState(DEFAULT_CARD_SIZE);

  const updateCardSize = useCallback((node: HTMLDivElement | null) => {
    if (!node) {
      return;
    }
    const rect = node.getBoundingClientRect();
    const nextSize = { width: rect.width, height: rect.height };
    setCardSize((prev) => (
      prev.width === nextSize.width && prev.height === nextSize.height ? prev : nextSize
    ));
  }, []);

  const placement = useMemo(
    () => resolveViewportOverlayPosition(anchorClientPosition, cardSize),
    [anchorClientPosition, cardSize]
  );

  const cardModel = useMemo(() => {
    if (!touchPoint) {
      return null;
    }

    const tags = touchPoint.slotTags || [];
    const isVerifyClusterTouchPoint = isVerifyPincodeTouchPoint(touchPoint);
    const isOrigin = tags.includes('ORIGIN') || touchPoint.name.toLowerCase().includes('origin');
    const isPickup = tags.includes('PICKUP');
    const id = (() => {
      const parts = (touchPoint.tpId || '').split('-');
      return isOrigin ? touchPoint.tpId : parts.length ? parts[parts.length - 1] : '';
    })();
    const loc = isOrigin ? lookups?.origin : isPickup ? lookups?.clients?.[id] : lookups?.drops?.[id];
    const address = (loc as any)?.address || touchPoint.address || '—';
    const typeLabel = isVerifyClusterTouchPoint ? 'Buyer' : isOrigin ? 'Origin' : isPickup ? 'Pickup' : 'Drop';
    const nextModel = buildTouchPointCardModel(touchPoint, {
      tenant,
      addressOverride: address,
      typeLabelOverride: typeLabel,
    });

    if (touchPoint.isHazmat) {
      nextModel.breachTags = [
        ...nextModel.breachTags,
        {
          key: 'hazmat',
          label: 'Hazmat',
          icon: 'warning',
          tone: 'danger',
          emphasis: 'soft',
        },
      ];
    }

    return nextModel;
  }, [lookups, tenant, touchPoint]);

  if (!touchPoint || !anchorClientPosition || !cardModel) {
    return null;
  }

  return (
    <Portal>
      <Paper
        ref={updateCardSize}
        elevation={3}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        sx={{
          position: 'fixed',
          left: placement.left,
          top: placement.top,
          p: 0,
          zIndex,
          maxWidth: 360,
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(15,23,42,0.12)',
          boxShadow: mode === 'dark'
            ? '0 10px 30px rgba(0,0,0,0.55)'
            : '0 10px 30px rgba(15,23,42,0.18)',
          pointerEvents: 'auto',
        }}
      >
        <Box sx={{ px: 1.2, py: 1.05 }}>
          <DenseEntityCardBody
            model={cardModel}
            density="compact"
            compactTitle
            inlineTitleWithLeading
            metricLayout="row"
            hideMetricDetails
            wrapSubtitle
            contentOrder={['primaryMetrics', 'highlightTags', 'capacityTags', 'breachTags', 'footer']}
            actions={
              touchPoint.geoCorrectionSupported && onStartGeoCorrection ? (
                <Tooltip title="Correct geo location">
                  <IconButton
                    size="small"
                    onClick={() => onStartGeoCorrection(touchPoint)}
                    sx={{
                      bgcolor: mode === 'dark' ? 'rgba(15,23,42,0.36)' : 'rgba(255,255,255,0.72)',
                      border: '1px solid',
                      borderColor: mode === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(15,23,42,0.14)',
                      '&:hover': {
                        bgcolor: mode === 'dark' ? 'rgba(15,23,42,0.56)' : 'rgba(255,255,255,0.92)',
                      },
                    }}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : null
            }
          />
        </Box>
      </Paper>
    </Portal>
  );
};

export default TouchPointPreviewOverlay;
